
import React from "react";
import { AlertTriangle } from "lucide-react";
import { CalendarEvent } from "@/hooks/calendar/types";
import { TimeSlot } from "./hooks/timeSlotHooks/types";
import SessionBookingDialog from "./SessionBookingDialog";
import ClientSessionRequestDialog from "./ClientSessionRequestDialog";
import { DeleteEventConfirmDialog } from "./modal/DeleteEventConfirmDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CalendarDialogsProps {
  selectedTimeSlot: TimeSlot | null;
  conflictingEvents: CalendarEvent[];
  isConflictDialogOpen: boolean;
  setIsConflictDialogOpen: (open: boolean) => void;
  isBookingDialogOpen: boolean;
  setIsBookingDialogOpen: (open: boolean) => void;
  isClientRequestDialogOpen: boolean;
  setIsClientRequestDialogOpen: (open: boolean) => void;
  isPastTimeSlotDialogOpen: boolean;
  setIsPastTimeSlotDialogOpen: (open: boolean) => void;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;
  eventToDelete: CalendarEvent | null;
  setEventToDelete: (event: CalendarEvent | null) => void;
  handleConflictConfirm: () => void;
  handleConflictCancel: () => void;
  handlePastTimeSlotConfirm: () => void;
  handlePastTimeSlotCancel: () => void;
  handleDeleteCancel: () => void;
  handleDeleteConfirm: () => Promise<void>;
  handleBookingClose: () => void;
  handleBookingConfirm: (clientId: string, packageId: string, title: string, remainingHours: number) => Promise<void>;
  handleClientRequestConfirm: (packageId: string, remainingHours: number) => Promise<void>;
  isClientView?: boolean;
  clientId?: string | null;
}

const CalendarDialogs: React.FC<CalendarDialogsProps> = ({
  selectedTimeSlot,
  conflictingEvents,
  isConflictDialogOpen,
  setIsConflictDialogOpen,
  isBookingDialogOpen,
  setIsBookingDialogOpen,
  isClientRequestDialogOpen,
  setIsClientRequestDialogOpen,
  isPastTimeSlotDialogOpen,
  setIsPastTimeSlotDialogOpen,
  deleteDialogOpen,
  setDeleteDialogOpen,
  eventToDelete,
  setEventToDelete,
  handleConflictConfirm,
  handleConflictCancel,
  handlePastTimeSlotConfirm,
  handlePastTimeSlotCancel,
  handleDeleteCancel,
  handleDeleteConfirm,
  handleBookingClose,
  handleBookingConfirm,
  handleClientRequestConfirm,
  isClientView = false,
  clientId = null
}) => {
  return (
    <>
      {/* Dialog di conferma per prenotazioni con conflitti */}
      <AlertDialog open={isConflictDialogOpen} onOpenChange={setIsConflictDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ci sono già eventi in questo slot</AlertDialogTitle>
            <AlertDialogDescription>
              Ci sono {conflictingEvents.length} eventi già programmati in questo slot orario.
              Vuoi procedere comunque con la prenotazione?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleConflictCancel}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleConflictConfirm}>Continua</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog di conferma per prenotazioni in slot passati */}
      <AlertDialog open={isPastTimeSlotDialogOpen} onOpenChange={setIsPastTimeSlotDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prenotazione in una data passata</AlertDialogTitle>
            <AlertDialogDescription>
              Stai cercando di prenotare una sessione in una data/ora già passata.
              Vuoi procedere comunque?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handlePastTimeSlotCancel}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handlePastTimeSlotConfirm}>Continua</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog per la prenotazione delle sessioni (per amministratori) */}
      {!isClientView && (
        <SessionBookingDialog
          open={isBookingDialogOpen}
          onOpenChange={setIsBookingDialogOpen}
          onClose={handleBookingClose}
          onConfirm={handleBookingConfirm}
          selectedDate={selectedTimeSlot?.date}
          selectedHour={selectedTimeSlot?.hour}
        />
      )}

      {/* Dialog per la richiesta di sessioni (per clienti) */}
      {isClientView && clientId && (
        <ClientSessionRequestDialog
          open={isClientRequestDialogOpen}
          onOpenChange={setIsClientRequestDialogOpen}
          onClose={handleBookingClose}
          onConfirm={handleClientRequestConfirm}
          selectedDate={selectedTimeSlot?.date}
          selectedHour={selectedTimeSlot?.hour}
          clientId={clientId}
        />
      )}

      {/* Dialog per l'eliminazione di un evento */}
      <DeleteEventConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        eventToDelete={eventToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default CalendarDialogs;
